import { useQueryClient } from '@tanstack/react-query';

import {
  useMeControllerEnviarFoto,
  useMeControllerEnviarLogo,
  useMeControllerRemoverFoto,
  useMeControllerRemoverLogo,
} from '@/api/generated/me/me';
import type { MeResponse } from '@/api/generated/model';
import { aoResponder } from '@/features/perfil/lib/aoResponder';
import { CampoDeFoto } from '@/shared/components/CampoDeFoto';
import { CampoDeImagem } from '@/shared/components/CampoDeImagem';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

/**
 * Foto de perfil e logo da clínica.
 *
 * ⚠️ Sem o logo, o receituário e a requisição saem só com texto no cabeçalho —
 * o `EmissorService` já lê `logoArquivoId` desde sempre, e era esta tela que
 * faltava para alguém preenchê-lo.
 */
export function ImagensDoPerfil({ me }: { me: MeResponse }) {
  const queryClient = useQueryClient();

  const enviarFoto = useMeControllerEnviarFoto();
  const removerFoto = useMeControllerRemoverFoto();
  const enviarLogo = useMeControllerEnviarLogo();
  const removerLogo = useMeControllerRemoverLogo();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="bg-surface-raised border-2 border-brand/20">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
            Foto de perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          <CampoDeFoto
            nome={me.perfil.nomeCompleto}
            urlAtual={me.perfil.fotoUrl}
            tamanho="lg"
            enviando={enviarFoto.isPending || removerFoto.isPending}
            aoEnviar={(arquivo) =>
              enviarFoto.mutate({ data: { arquivo } }, aoResponder(queryClient, 'Foto atualizada.'))
            }
            aoRemover={() =>
              removerFoto.mutate(undefined, aoResponder(queryClient, 'Foto removida.'))
            }
          />
          <p className="text-xs text-muted-foreground text-center">
            Clique na foto para trocar. Aparece no topo e nas discussões; não vai para os documentos.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-surface-raised border-2 border-brand/20">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
            Logo da clínica
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CampoDeImagem
            rotulo="Logo"
            ajuda="PNG ou JPG com ao menos 200px de largura. Até 2 MB. Vai no cabeçalho dos receituários e das requisições de exame."
            urlAtual={me.perfil.logoUrl}
            proporcao="aspect-[3/1]"
            enviando={enviarLogo.isPending || removerLogo.isPending}
            aoEnviar={(arquivo) =>
              enviarLogo.mutate({ data: { arquivo } }, aoResponder(queryClient, 'Logo atualizado.'))
            }
            aoRemover={() =>
              removerLogo.mutate(undefined, aoResponder(queryClient, 'Logo removido.'))
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
